/**
 * mergeServerComment
 *
 * Reconciles a live Firestore emission of a comment against the locally-held
 * copy: server truth for every persisted field, local truth for the transient
 * thread-state fields the client owns (`commentEntity.ts` "Transient UI fields
 * (not persisted)" block).
 */

import type { CommentEntity } from '@/domain/entities/comment/commentEntity';

/**
 * Merge a live server emission over the locally-held comment entity.
 *
 * Flutter replaces the list entry wholesale (`_comments[index] = comment`,
 * comments_bottom_sheet.dart:879), which resets every transient field to
 * `Comment.fromJson`'s defaults. That is harmless there only because the
 * watched comment is always a freshly-created leaf reply with no children and
 * no expansion state. Web preserves those fields deliberately so an incoming
 * emission can never collapse an expanded thread; every persisted field —
 * including the `mediaInfo` the watch listener exists for — still comes from
 * the server entity.
 */
export function mergeServerComment(
  local: CommentEntity,
  server: CommentEntity,
): CommentEntity {
  return {
    ...server,
    parent: local.parent,
    children: local.children,
    lastFetchedChild: local.lastFetchedChild,
    hasMoreChildren: local.hasMoreChildren,
    childrenFetched: local.childrenFetched,
    isLastChild: local.isLastChild,
    isFetchingChildren: local.isFetchingChildren,
    isInitialComment: local.isInitialComment,
  };
}
